import {
  Box,
  CircularProgress,
  Step,
  StepLabel,
  Stepper,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useCounterPartyController } from 'api/controllers/CounterPartyController';
import { useInvoiceController } from 'api/controllers/InvoiceController';

import {
  CounterpartyCreateUpdate,
  CounterpartyType,
  EntryType,
  Invoice,
  InvoicePatch,
  InvoiceSection,
  InvoiceStages
} from 'openapi';

import { Approvals } from 'components/forms/InvoiceVerificationForms/Approvals';
import { CreateCounterpartyOverview } from 'components/forms/InvoiceVerificationForms/CreateCounterpartyOverview';
import { CreatedDocumentForm } from 'components/forms/InvoiceVerificationForms/CreatedDocumentForm';

import { useTranslations } from 'context/TranslationContext';

import { getConfigByDocumentType } from 'utils/config/documentEntries';
import { CREATED_DOCUMENT_STEPPER_SECTIONS } from 'utils/constants/invoices';
import { InvoiceSectionKey } from 'utils/interfaces/InvoiceProps';

import { loaderWithBackdropWrapper } from 'styles/components/LoaderStyle';

import { isRequestPendingAtom } from 'state/state';

const CREATED_DOCUMENT_APPROVALS_STEP = 1;

const getCreatedDocumentActiveStep = (invoice: Invoice) =>
  invoice.stage === InvoiceStages.DRAFT ||
  invoice.stage === InvoiceStages.UPLOADED
    ? 0
    : 1;

interface CreatedDocumentVerificationStepperProps {
  currentInvoice: Invoice;
  triggeredReprompt: boolean;
  setCurrentInvoice: React.Dispatch<React.SetStateAction<Invoice | null>>;
  refreshInvoice: (newCompanyId: number) => Promise<void>;
}

export const CreatedDocumentVerificationStepper = ({
  currentInvoice,
  triggeredReprompt,
  setCurrentInvoice,
  refreshInvoice
}: CreatedDocumentVerificationStepperProps) => {
  const { translate } = useTranslations();
  const {
    unverifyInvoiceById,
    saveInvoiceData,
    verifyCreatedDocument,
    returnToValidatedById,
    approveInvoicesByIds,
    rejectInvoicesByIds,
    settleInvoices
  } = useInvoiceController();
  const { createNewCounterParty } = useCounterPartyController();

  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.up('sm'));

  const [activeStep, setActiveStep] = useState(
    getCreatedDocumentActiveStep(currentInvoice)
  );
  const [isOnMiddleStep, setIsOnMiddleStep] = useState(false);

  useEffect(() => {
    setActiveStep(getCreatedDocumentActiveStep(currentInvoice));
  }, [currentInvoice.stage]);

  const documentConfig = useMemo(
    () => getConfigByDocumentType(currentInvoice.entryType as EntryType),
    [currentInvoice.entryType]
  );
  const [documentFormData, setDocumentFormData] = useState<Invoice>();
  const [cpIsNotFoundInDatabase, setCpIsNotFoundInDatabase] = useState(false);
  const [, setRequestPending] = useAtom(isRequestPendingAtom);

  const handleBack = async (section: InvoiceSection) => {
    setRequestPending(true);
    try {
      await unverifyInvoiceById(
        Number(currentInvoice.companyId),
        Number(currentInvoice.id),
        section
      );
      refreshInvoice(currentInvoice.companyId!);
      setActiveStep((prevActiveStep) => prevActiveStep - 1);
    } finally {
      setRequestPending(false);
    }
  };

  const saveCreatedDocumentData = useCallback(
    async (values: InvoicePatch) => {
      if (!currentInvoice.id || !currentInvoice.companyId) return;
      setRequestPending(true);
      try {
        const result = await saveInvoiceData(
          currentInvoice.companyId,
          currentInvoice.id,
          values
        );
        setCurrentInvoice(result);
      } finally {
        setRequestPending(false);
      }
    },
    [saveInvoiceData, currentInvoice.id, currentInvoice.companyId]
  );

  const handleVerifyCreatedDocument = useCallback(
    async (values: InvoicePatch) => {
      if (!currentInvoice.id || !currentInvoice.companyId) return;

      let { counterpartyId } = values;

      if (!counterpartyId && values.counterpartyName) {
        const createBody: CounterpartyCreateUpdate = {
          name: values.counterpartyName,
          vatNumber: values.counterpartyVatNumber,
          registrationNumber: values.counterpartyRegistrationNumber,
          ibans: [],
          type: CounterpartyType.SUPPLIER
        };

        try {
          const createdCounterparty = await createNewCounterParty(
            currentInvoice.companyId,
            createBody
          );
          counterpartyId = createdCounterparty.id;
        } catch {
          return;
        }
      }

      await verifyCreatedDocument(currentInvoice.companyId, currentInvoice.id, {
        ...values,
        counterpartyId
      });
    },
    [
      createNewCounterParty,
      verifyCreatedDocument,
      currentInvoice.id,
      currentInvoice.companyId
    ]
  );

  const handleApprove = async () => {
    setRequestPending(true);
    try {
      const result = await approveInvoicesByIds([Number(currentInvoice.id)]);
      setCurrentInvoice(result);
      setActiveStep(CREATED_DOCUMENT_APPROVALS_STEP);
    } finally {
      setRequestPending(false);
    }
  };

  const handleSettle = async () => {
    setRequestPending(true);
    try {
      const result = await settleInvoices({
        ids: [Number(currentInvoice.id)]
      });
      setCurrentInvoice(result);
      setActiveStep(CREATED_DOCUMENT_APPROVALS_STEP);
    } finally {
      setRequestPending(false);
    }
  };

  const handleReject = async (reason: string) => {
    setRequestPending(true);
    try {
      await rejectInvoicesByIds([Number(currentInvoice.id!)], reason);
      await refreshInvoice(Number(currentInvoice.companyId));
      setActiveStep(CREATED_DOCUMENT_APPROVALS_STEP);
    } finally {
      setRequestPending(false);
    }
  };

  const handleReturnToValidated = async () => {
    setRequestPending(true);
    try {
      const result = await returnToValidatedById(
        Number(currentInvoice.companyId),
        Number(currentInvoice.id)
      );
      setActiveStep((prev) => prev - 1);
      setCurrentInvoice(result);
    } finally {
      setRequestPending(false);
    }
  };

  const handleCreatedDocumentBack = useCallback(
    () => handleBack(InvoiceSection.COMPANY),
    [handleBack]
  );

  const handleGoToMiddleStep = useCallback(() => {
    setIsOnMiddleStep(true);
  }, []);

  const handleBackFromMiddleStep = useCallback(() => {
    setIsOnMiddleStep(false);
  }, []);

  const handleSaveData = useCallback(
    async (
      values: InvoicePatch,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _section: InvoiceSectionKey = InvoiceSection.SUMMARY
    ) => {
      if (!currentInvoice.id || !currentInvoice.companyId) return;
      await saveInvoiceData(
        currentInvoice.companyId,
        currentInvoice.id,
        values
      );
    },
    [saveInvoiceData, currentInvoice.id, currentInvoice.companyId]
  );

  return (
    <Box sx={{ width: '100%', mb: 2.5 }}>
      <Stepper activeStep={activeStep}>
        {CREATED_DOCUMENT_STEPPER_SECTIONS.map((labelKey: string) => {
          return (
            <Step key={labelKey}>
              <StepLabel>{isSm ? translate(labelKey) : ''}</StepLabel>
            </Step>
          );
        })}
      </Stepper>

      <Box sx={{ position: 'relative' }}>
        {activeStep === 0 && (
          <Box>
            {isOnMiddleStep ? (
              <CreateCounterpartyOverview
                currentInvoice={currentInvoice}
                counterpartyFormData={documentFormData}
                handleBack={handleBackFromMiddleStep}
                handleCreate={handleVerifyCreatedDocument}
                cpIsNotFoundInDatabase={cpIsNotFoundInDatabase}
              />
            ) : (
              <CreatedDocumentForm
                documentConfig={documentConfig}
                currentInvoice={currentInvoice}
                handleVerify={handleVerifyCreatedDocument}
                handleSave={saveCreatedDocumentData}
                handleGoToMiddleStep={handleGoToMiddleStep}
                setDocumentFormData={setDocumentFormData}
                setCpIsNotFoundInDatabase={setCpIsNotFoundInDatabase}
              />
            )}
          </Box>
        )}
        {activeStep === 1 && (
          <Approvals
            documentConfig={documentConfig}
            currentInvoice={currentInvoice}
            handleBack={handleCreatedDocumentBack}
            handleBackFromMiddleStep={handleBackFromMiddleStep}
            handleApprove={handleApprove}
            handleReject={handleReject}
            handleReturnToValidated={handleReturnToValidated}
            handleSettle={handleSettle}
            handleSaveData={handleSaveData}
          />
        )}

        {triggeredReprompt && (
          <Box sx={loaderWithBackdropWrapper}>
            <CircularProgress />
          </Box>
        )}
      </Box>
    </Box>
  );
};
